import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Modal,
  ProgressBar,
  Row,
  Spinner,
  Table,
  Tab,
  Tabs,
} from "react-bootstrap";
import { Redirect } from "react-router-dom";
import { FaTrash, FaInfoCircle, FaTimesCircle } from "react-icons/fa";
import SidebarPanelNavigation from "../../../../components/SidebarPanelNavigation";
import useAuth from "../../../../contexts/auth";
import {
  listWhatsAppWebCampaigns,
  getWhatsAppWebCampaignDispatches,
  cancelWhatsAppWebCampaign,
  deleteWhatsAppWebCampaign,
} from "../../../../services/api";

const TAB_FILTERS = {
  andamento: "pending,queued,sending",
  agendadas: "scheduled",
  historico: "sent,error,cancelled,interrupted",
};

const STATUS_VARIANT = {
  sent: "success",
  pending: "warning",
  queued: "warning",
  sending: "info",
  scheduled: "info",
  error: "danger",
  cancelled: "secondary",
  interrupted: "secondary",
};

export default function CampaignsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("andamento");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null); // { campaign, dispatches }

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listWhatsAppWebCampaigns({
        page: 1,
        pageSize: 50,
        status: TAB_FILTERS[tab],
      });
      setItems(res?.data || []);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Polling enquanto na aba "andamento"
  useEffect(() => {
    if (tab !== "andamento") return undefined;
    const i = setInterval(reload, 5000);
    return () => clearInterval(i);
  }, [tab, reload]);

  if (!user) return <Redirect to="/entrar" />;
  if (user.type !== "admin") return <Redirect to="/" />;

  async function openDetails(c) {
    const res = await getWhatsAppWebCampaignDispatches(c.id);
    setDetails({ campaign: c, dispatches: res?.data || [] });
  }

  async function handleCancel(c) {
    if (!window.confirm(`Cancelar campanha "${c.name}"?`)) return;
    await cancelWhatsAppWebCampaign(c.id);
    reload();
  }

  async function handleDelete(c) {
    if (!window.confirm(`Excluir campanha "${c.name}"?`)) return;
    await deleteWhatsAppWebCampaign(c.id);
    reload();
  }

  return (
    <Container fluid className="p-4">
      <Row>
        <Col md={3} lg={2}>
          <SidebarPanelNavigation isAdmin />
        </Col>
        <Col md={9} lg={10}>
          <h2 className="mb-3">📣 Campanhas</h2>

          <Tabs activeKey={tab} onSelect={setTab} className="mb-3">
            <Tab eventKey="andamento" title="Andamento" />
            <Tab eventKey="agendadas" title="Agendadas" />
            <Tab eventKey="historico" title="Histórico" />
          </Tabs>

          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" />
            </div>
          ) : items.length === 0 ? (
            <Card className="text-center p-5">
              <Card.Body>
                {tab === "andamento" && (
                  <>
                    <div style={{ fontSize: 48 }}>⚡</div>
                    <h5>Tudo tranquilo por aqui</h5>
                    <p className="text-muted">
                      Nenhuma campanha em execução no momento.
                    </p>
                  </>
                )}
                {tab === "agendadas" && (
                  <>
                    <div style={{ fontSize: 48 }}>📅</div>
                    <h5>Nenhum agendamento</h5>
                    <p className="text-muted">
                      Você ainda não tem campanhas agendadas.
                    </p>
                  </>
                )}
                {tab === "historico" && (
                  <p className="text-muted">Nenhuma campanha encontrada.</p>
                )}
              </Card.Body>
            </Card>
          ) : (
            items.map((c) => (
              <Card key={c.id} className="mb-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h5 className="mb-0">{c.name}</h5>
                      <small className="text-muted">
                        Segmento: {c.segment_name || "—"}
                      </small>
                    </div>
                    <div>
                      <Badge variant={STATUS_VARIANT[c.status] || "secondary"}>
                        {c.status}
                      </Badge>
                    </div>
                  </div>

                  <Row>
                    <Col md={2}>
                      <small className="text-muted">PEND.</small>
                      <h4 className="text-warning mb-0">
                        {c.pending_groups || 0}
                      </h4>
                    </Col>
                    <Col md={2}>
                      <small className="text-muted">ENVIADAS</small>
                      <h4 className="text-success mb-0">
                        {c.sent_groups || 0}
                      </h4>
                    </Col>
                    <Col md={2}>
                      <small className="text-muted">ERROS</small>
                      <h4 className="text-danger mb-0">
                        {c.error_groups || 0}
                      </h4>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted">CRIADO</small>
                      <div>
                        {new Date(c.created_at).toLocaleString("pt-BR")}
                      </div>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted">AGENDADA</small>
                      <div>
                        {c.scheduled_at
                          ? new Date(c.scheduled_at).toLocaleString("pt-BR")
                          : "—"}
                      </div>
                    </Col>
                  </Row>

                  {c.total_groups > 0 && (
                    <ProgressBar
                      className="mt-2"
                      now={
                        ((c.sent_groups || 0) + (c.error_groups || 0)) /
                        c.total_groups *
                        100
                      }
                    />
                  )}

                  <div className="mt-3 d-flex" style={{ gap: 6 }}>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => openDetails(c)}
                    >
                      <FaInfoCircle /> Detalhes
                    </Button>
                    {["pending", "queued", "sending", "scheduled"].includes(
                      c.status
                    ) && (
                      <Button
                        size="sm"
                        variant="outline-warning"
                        onClick={() => handleCancel(c)}
                      >
                        <FaTimesCircle /> Cancelar
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleDelete(c)}
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            ))
          )}
        </Col>
      </Row>

      {/* Modal Detalhes */}
      <Modal
        show={!!details}
        onHide={() => setDetails(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Detalhes da Campanha</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {details && (
            <>
              <h6>Mensagem</h6>
              <Card className="mb-3">
                <Card.Body
                  style={{ whiteSpace: "pre-wrap", maxHeight: 200, overflowY: "auto" }}
                >
                  {details.campaign.message || "(sem texto)"}
                </Card.Body>
              </Card>

              <h6>Disparos por grupo</h6>
              <Table striped size="sm" responsive>
                <thead>
                  <tr>
                    <th>Grupo</th>
                    <th>Canal</th>
                    <th>Status</th>
                    <th>Envio</th>
                    <th>Visualização</th>
                  </tr>
                </thead>
                <tbody>
                  {details.dispatches.map((d) => (
                    <tr key={d.id}>
                      <td>{d.group_name || d.group_jid}</td>
                      <td>{d.channel_name}</td>
                      <td>
                        <Badge variant={STATUS_VARIANT[d.status] || "secondary"}>
                          {d.status}
                        </Badge>
                      </td>
                      <td>
                        {d.sent_at
                          ? new Date(d.sent_at).toLocaleString("pt-BR")
                          : "—"}
                      </td>
                      <td>
                        {d.read_count > 0 ? (
                          <Badge variant="success">{d.read_count}</Badge>
                        ) : (
                          <Badge variant="secondary">Nenhuma</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
}
